import React, { useEffect, useState, forwardRef, useCallback, useMemo } from "react";
import { Table, Pagination, Flex, Spin } from "antd";
import {usePaginationRoving} from "@hooks/useIndexRoving";
import {getAccessibleItemRender} from "./paginationUtils";
import {mergeReactRefs} from "../utilityFunctions";

const DEFAULT_PAGE_SIZE = 10,
    DEFAULT_CURRENT_PAGE = 1;
const withAntDPagination = (WrappedComponent, fetchFunction, params, onReady) => {
    return forwardRef((props, ref) => {
        let { dynamicWidth = false, scroll, loading: parentLoading, reload, position, roving, ...restProps } = props;

        let { pageSize, currentPage } = params;
        if (!pageSize) {
            pageSize = DEFAULT_PAGE_SIZE;
        }
        if (!currentPage) {
            currentPage = DEFAULT_CURRENT_PAGE;
        }

        const [dataSource, setDataSource] = useState([]);
        const [pageData, setPageData] = useState({ currentPage: currentPage, totalResults: 0, pageSize: pageSize });
        const [loading, setLoading] = useState(false);
        const [showOnePageTotal, setShowOnePageTotal] = useState(false);

        const paginationRoving = usePaginationRoving();

        const handleSetData = (data) => {
            setDataSource(data);
            setLoading(false);
        };

        const handlePageData = (updatePageData) => {
            const { pageSize, totalResults } = updatePageData;
            setPageData(prev => ({ ...prev, totalResults, pageSize }));
            setShowOnePageTotal(totalResults && totalResults <= pageSize);
        };


        useEffect(() => {
            if (
                pageData &&
                pageData.currentPage > 1 &&
                pageData.currentPage > Math.ceil(pageData.totalResults / pageData.pageSize)
            ) {
                setPageData(prev => ({ ...prev, currentPage: 1 }));
            }
        }, [pageData.currentPage, pageData.totalResults, pageData.pageSize]);

        const getData = (abortController) => {
            if (pageData.currentPage && !isNaN(pageData.currentPage)) {
                const fetchParams = { pageNumber: pageData.currentPage, pageSize: pageData.pageSize, ...params };
                fetchFunction(fetchParams, handleSetData, handlePageData, abortController);
            } else {
                setLoading(false);
            }
        };

        useEffect(() => {
            setLoading(true);
            const abortController = new AbortController();
            getData(abortController);

            return () => {
                if (abortController) {
                    abortController.abort();
                }
            };
        }, [pageData.currentPage, pageData.pageSize]);

        useEffect(() => {
            setLoading(true);
            const abortController = new AbortController();
            if (reload != null) {
                getData(abortController);
            }
            return () => {
                if (abortController) {
                    abortController.abort();
                }
            };
        }, [reload]);

        useEffect(() => {
            if (dataSource) {
                if (onReady && typeof onReady === "function") {
                    requestAnimationFrame(() => {
                        onReady(dataSource);
                        setLoading(false);
                    });
                }
            }
        }, [dataSource]);

        const hasRows = dataSource && dataSource.length > 0;
        const effectiveScroll = hasRows ? scroll : undefined;
        const isTable = WrappedComponent === Table || !!restProps.columns;

        const handlePageChange = useCallback((currentPage, pageSize) => {
            setPageData(prev => ({ ...prev, pageSize, currentPage }));
        }, []);

        const paginationConfig = useMemo(() =>{
            const customItemRender = getAccessibleItemRender({
                currentPage: pageData.currentPage,
                totalResults: pageData.totalResults,
                pageSize: pageData.pageSize,
                showOnePageTotal: showOnePageTotal
            });
            return {
                showSizeChanger: false,
                current: pageData.currentPage,
                total: pageData.totalResults,
                pageSize: pageData.pageSize,
                hideOnSinglePage: !pageData || pageData.totalResults === 0,
                onChange: handlePageChange,
                size: "small",
                showTotal: (total, range) =>
                    (showOnePageTotal ? `` : `${range[0]}-${range[1]} of `) + `${total} item` + (pageData.totalResults > 1 ? `s` : ``),
                itemRender: customItemRender
            }
        }, [pageData, showOnePageTotal, handlePageChange]);

        // TABLE LOGIC
        if (isTable) {
            return (
                <div ref={mergeReactRefs(ref, paginationRoving.ref)}
                     className={'ch-pagination-wrapper'}
                     onKeyDown={paginationRoving.onKeyDown}
                     onFocus={paginationRoving.onFocus}
                     onClick={paginationRoving.onClick}
                     aria-busy={loading || parentLoading} aria-live="polite">
                    <WrappedComponent
                        dataSource={dataSource}
                        {...restProps}
                        scroll={effectiveScroll}
                        loading={(loading || parentLoading)}
                        size={'small'}
                        role="grid"
                        pagination={{ ...paginationConfig }}
                    />
                </div>
            );
        }

        // FLEX/LIST LOGIC
        return (
            <div ref={ref} className="ch-pagination-wrapper">
                <div aria-live="polite" aria-busy={loading || parentLoading}>
                    <Spin spinning={!!(loading || parentLoading)}>
                        {(dataSource.length > 0) ? (
                            <WrappedComponent
                                {...restProps}
                                vertical
                                gap="small"
                                role="list"
                                {...roving}
                                ref={roving?.ref}
                            >
                                {dataSource.map((item, index) => (
                                    typeof restProps.children === 'function' ? restProps.children(item, index) : null
                                ))}
                            </WrappedComponent>
                        ) : (
                            <Flex justify="center">
                                {restProps.locale && restProps.locale?.emptyText}
                            </Flex>
                        )}
                    </Spin>
                </div>

                {(!pageData || pageData.totalResults > 0) && (
                    <Flex
                        ref={paginationRoving.ref}
                        onKeyDown={paginationRoving.onKeyDown}
                        onFocus={paginationRoving.onFocus}
                        onClick={paginationRoving.onClick}
                        justify="end"
                        style={{ marginTop: 16 }}
                        // Semantic wrapper for navigation
                        component="nav"
                        aria-label="Pagination Navigation"
                    >
                        <Pagination
                            {...paginationConfig}
                        />
                    </Flex>
                )}
            </div>
        );
    });
}

export default withAntDPagination;